'use client';

import * as React from 'react';
import Link from 'next/link';

import { AudioTranscriptionEventBus } from '@audio-transcription/core/AudioTranscriptionEventBus';
import {
  AcceptTranscriptionOptions,
  TranscriptionState,
  UseAudioTranscriptionOptions,
  UseAudioTranscriptionValue,
} from '@audio-transcription/core/AudioTranscriptionTypes';
import { toast } from '@core/ui/hooks/use-toast';
import { ToastAction } from '@core/ui/primitives/toast';

const DEFAULT_MAX_BYTES = 25 * 1024 * 1024; // 25MB ceiling

function inferVaultContextFromPath(pathname: string): { vault?: string; org?: string } | null {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length >= 2 && segments[0] === 'v') {
    return { vault: segments[1] };
  }
  if (segments.length >= 2) {
    const [first, second] = segments;
    const reserved = new Set(['welcome', 'admin', 'api', 'no-access', 'auth', 'mcp']);
    if (!reserved.has(first)) {
      return { org: first, vault: second };
    }
  }
  return null;
}

function pickMimeType(): string {
  const types = ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/mp4'];
  for (const type of types) {
    const MediaRecorderImpl = (window as any).MediaRecorder;
    if (MediaRecorderImpl?.isTypeSupported?.(type)) {
      return type;
    }
  }
  return 'audio/webm';
}

export function useAudioTranscription(options: UseAudioTranscriptionOptions = {}): UseAudioTranscriptionValue {
    const {
      language,
      model,
      prompt,
      onError,
      onTranscribed,
      maxBytes = DEFAULT_MAX_BYTES,
      source = 'unknown',
    } = options;

    const [state, setState] = React.useState<TranscriptionState>('idle');
    const [supported, setSupported] = React.useState(true);
    const [seconds, setSeconds] = React.useState(0);
    const [error, setError] = React.useState<string | null>(null);
    const secondsRef = React.useRef(0);

    const chunksRef = React.useRef<BlobPart[]>([]);
    const mediaRef = React.useRef<MediaRecorder | null>(null);
    const streamRef = React.useRef<MediaStream | null>(null);
    const analyserRef = React.useRef<AnalyserNode | null>(null);
    const audioCtxRef = React.useRef<AudioContext | null>(null);
    const dataArrayRef = React.useRef<Uint8Array | null>(null);
    const bufferLengthRef = React.useRef<number>(0);
    const cancelRef = React.useRef<boolean>(false);
    const extraFieldsRef = React.useRef<Record<string, string> | null>(null);
    const dynamicOnTranscribedRef = React.useRef<((text: string) => void) | undefined>(onTranscribed);
    const dynamicOnErrorRef = React.useRef<(message: string) => void | undefined>(onError);
    const sourceRef = React.useRef<string>(source);

    React.useEffect(() => {
      setSupported(!!(typeof navigator !== 'undefined' && navigator.mediaDevices && (window as any).MediaRecorder));
    }, []);

    React.useEffect(() => {
      dynamicOnTranscribedRef.current = onTranscribed;
    }, [onTranscribed]);

    React.useEffect(() => {
      dynamicOnErrorRef.current = onError;
    }, [onError]);

    React.useEffect(() => {
      sourceRef.current = source;
    }, [source]);

    React.useEffect(() => {
      if (state === 'recording') {
        const id = window.setInterval(() => setSeconds((prev) => prev + 1), 1000);
        return () => window.clearInterval(id);
      }
      return undefined;
    }, [state]);

    React.useEffect(() => {
      secondsRef.current = seconds;
    }, [seconds]);

    const clearError = React.useCallback(() => setError(null), []);

    const cleanupAudio = React.useCallback(() => {
      if (audioCtxRef.current) {
        try {
          audioCtxRef.current.close();
        } catch {
          /* no-op */
        }
        audioCtxRef.current = null;
      }

      if (analyserRef.current) {
        try {
          analyserRef.current.disconnect();
        } catch {
          /* no-op */
        }
        analyserRef.current = null;
      }

      if (streamRef.current) {
        for (const track of streamRef.current.getTracks()) {
          try {
            track.stop();
          } catch {
            /* no-op */
          }
        }
        streamRef.current = null;
      }
    }, []);

    const reset = React.useCallback(() => {
      cleanupAudio();
      chunksRef.current = [];
      setState('idle');
      setSeconds(0);
      secondsRef.current = 0;
      cancelRef.current = false;
    }, [cleanupAudio]);

    const drawAnalyser = React.useCallback(() => {
      const analyser = analyserRef.current;
      if (!analyser) return;
      const bufferLength = analyser.frequencyBinCount;
      bufferLengthRef.current = bufferLength;
      dataArrayRef.current = new Uint8Array(bufferLength);
    }, []);

    const acceptOverrideRef = React.useRef<((text: string) => void) | undefined>(undefined);

    const handleStopped = React.useCallback(async () => {
      cleanupAudio();

      try {
        if (cancelRef.current) {
          chunksRef.current = [];
          cancelRef.current = false;
          setState('idle');
          setSeconds(0);
          return;
        }

        const mime = pickMimeType();
        const blob = new Blob(chunksRef.current, { type: mime });
        chunksRef.current = [];

        if (blob.size > maxBytes) {
          const message = 'Recording too large (>25MB). Please try again.';
          setError(message);
          dynamicOnErrorRef.current?.(message);
          setState('idle');
          return;
        }

        const formData = new FormData();
        const filename = mime.includes('webm') ? 'audio.webm' : mime.includes('ogg') ? 'audio.ogg' : 'audio.mp4';
        formData.append('file', blob, filename);
        if (language) formData.append('language', language);
        if (model) formData.append('model', model);
        if (prompt) formData.append('prompt', prompt);

        const extra = extraFieldsRef.current;
        if (extra) {
          for (const [key, value] of Object.entries(extra)) {
            formData.append(key, value);
          }
        }
        extraFieldsRef.current = null;

        // NOTE: durationSeconds is no longer sent - server measures duration from OpenAI response

        if (typeof window !== 'undefined') {
          const inferred = inferVaultContextFromPath(window.location.pathname);
          if (inferred?.vault && !formData.has('vault')) {
            formData.append('vault', inferred.vault);
          }
          if (inferred?.org && !formData.has('org')) {
            formData.append('org', inferred.org);
          }
        }

        const response = await fetch('/api/transcribe', { method: 'POST', body: formData });
        const data = await response.json();
        if (!response.ok) {
          const message = (data?.error as string) || 'Transcription failed';
          if (data?.code === 'TRANSCRIPTION_QUOTA_EXCEEDED') {
            const resetDateRaw = typeof data?.resetDate === 'string' ? data.resetDate : null;
            const resetLabel = resetDateRaw ? new Date(resetDateRaw).toLocaleDateString() : 'soon';
            toast({
              variant: 'destructive',
              title: 'Monthly transcription limit reached',
              description: `You've reached your organization's monthly transcription limit. Resets on ${resetLabel}.`,
              action: (
                <ToastAction altText='Upgrade' asChild>
                  <Link href='/welcome#pricing'>Upgrade</Link>
                </ToastAction>
              ),
            });
          }
          setError(message);
          dynamicOnErrorRef.current?.(message);
          setState('idle');
          return;
        }

        const text = String(data?.text || '').trim();
        if (text) {
          dynamicOnTranscribedRef.current?.(text);
          const onTranscribedOverride = acceptOverrideRef.current;
          acceptOverrideRef.current = undefined;
          onTranscribedOverride?.(text);
          AudioTranscriptionEventBus.emit({
            text,
            source: sourceRef.current,
            language: (data?.language as string | undefined) ?? language ?? null,
            model: model || 'whisper-1',
            timestamp: Date.now(),
            raw: data,
          });
        }
        setError(null);
      } catch (err: any) {
        const message = err?.message || 'Failed to transcribe';
        setError(message);
        dynamicOnErrorRef.current?.(message);
      } finally {
        setState('idle');
      }
    }, [cleanupAudio, language, maxBytes, model, prompt]);

    const startRecording = React.useCallback(async () => {
      setError(null);
      if (!supported) {
        const message = 'Microphone not supported in this browser.';
        setError(message);
        dynamicOnErrorRef.current?.(message);
        return;
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        streamRef.current = stream;

        const mediaRecorder = new MediaRecorder(stream, { mimeType: pickMimeType() });
        mediaRef.current = mediaRecorder;
        chunksRef.current = [];

        const AudioContextImpl = window.AudioContext || (window as any).webkitAudioContext;
        const audioCtx = new AudioContextImpl();
        audioCtxRef.current = audioCtx;
        const mediaSource = audioCtx.createMediaStreamSource(stream);
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 2048;
        analyserRef.current = analyser;
        mediaSource.connect(analyser);
        drawAnalyser();

        mediaRecorder.ondataavailable = (event) => {
          if (event.data && event.data.size > 0) {
            chunksRef.current.push(event.data);
          }
        };
        mediaRecorder.onstop = () => {
          void handleStopped();
        };

        mediaRecorder.start();
        setSeconds(0);
        setState('recording');
      } catch (err: any) {
        const message = err?.message || 'Unable to access microphone';
        setError(message);
        dynamicOnErrorRef.current?.(message);
      }
    }, [drawAnalyser, handleStopped, supported]);

    const acceptRecording = React.useCallback(
      (acceptOptions?: AcceptTranscriptionOptions) => {
        const recorder = mediaRef.current;
        if (!recorder) return;
        acceptOverrideRef.current = acceptOptions?.onTranscribed;
        extraFieldsRef.current = acceptOptions?.extraFields ?? null;
        cancelRef.current = false;
        setState('processing');
        try {
          recorder.stop();
        } catch {
          setState('idle');
        }
      },
      [],
    );

    const cancelRecording = React.useCallback(() => {
      const recorder = mediaRef.current;
      cancelRef.current = true;
      if (!recorder) {
        reset();
        return;
      }
      try {
        recorder.stop();
      } catch {
        reset();
      }
    }, [reset]);

    const value = React.useMemo<UseAudioTranscriptionValue>(
      () => ({
        state,
        supported,
        seconds,
        error,
        analyser: analyserRef.current,
        dataArray: dataArrayRef.current,
        bufferLength: bufferLengthRef.current,
        startRecording,
        acceptRecording,
        cancelRecording,
        clearError,
        reset,
      }),
      [state, supported, seconds, error, startRecording, acceptRecording, cancelRecording, clearError, reset],
    );

    return value;
  }

export const AudioTranscriptionHook = {
  useAudioTranscription,
} as const;
